/**
 * /portal/login - email + password (+ TOTP step when enrolled).
 *
 * Two-step flow:
 *   1. Email + password -> POST /v1/portal/auth/login. On 200 with
 *      `status: 'authenticated'` the AuthContext is updated and we
 *      bounce to the previously-requested path. On 200 with
 *      `status: 'totp_required'` we keep the login_token in local
 *      state and switch the form to the TOTP step.
 *   2. TOTP code -> POST /v1/portal/auth/login/totp with the login_token
 *      from step 1. Success bounces to the requested path.
 *
 * Wrong-credentials is 401 with `error: 'invalid_credentials'` -
 * rendered as a generic "wrong email or password" so the surface
 * doesn't disclose which factor failed (defense matches /auth/login
 * on the admin side).
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../lib/api';
import { ApiError, useAuth } from '../lib/auth-context';
import { OrbotoLockup } from '../components/OrbotoLockup';
import { LegalFooter } from '../components/LegalFooter';

interface LocationState {
  from?: string;
}

type Step = 'credentials' | 'totp';

/**
 * Reject open-redirect targets - must be a same-site path. Mirror of the
 * helper in SignupPage so the two auth flows have identical defenses.
 */
function safeRedirectTarget(raw: string | null | undefined): string {
  if (!raw) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  // eslint-disable-next-line no-control-regex -- intentional: reject control chars in the redirect target (open-redirect / header-injection defense)
  if (/[\x00-\x1f]/.test(raw)) return '/';
  return raw;
}

export function LoginPage() {
  const { t } = useTranslation('login');
  const { status, user, login, loginTotp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // OCP-264 - accept `?from=` in the URL too. ProtectedRoute uses
  // location.state.from for in-app deep-link recoveries, but a
  // chooser link from /redeem (or any external "log in to claim this"
  // CTA) lands here without router state, so we fall back to the
  // query param. Both go through safeRedirectTarget to block
  // open-redirect attempts.
  const stateFrom = (location.state as LocationState | null)?.from;
  const queryFrom = searchParams.get('from');
  const from = safeRedirectTarget(stateFrom ?? queryFrom);

  const [step, setStep] = useState<Step>('credentials');
  const [loginToken, setLoginToken] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // When the API returns email_not_verified, surface a resend-link
  // button so the customer can request a fresh verification email
  // without having to navigate elsewhere. Server-side rate limit
  // (3 per 5min) defends against spam abuse.
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState<
    null | 'pending' | 'sent' | 'rate_limited' | 'failed'
  >(null);

  // Post-auth navigation, centralised here so both the credentials + TOTP
  // steps (and an already-signed-in visit) route identically. Runs once the
  // AuthContext commits `authenticated` + the user identity.
  //
  // OCP-515 - a fresh-device login for an account carrying an unclaimed
  // redeem code, with NO explicit redeem target in the URL, continues to the
  // claim instead of dropping the customer on the overview (empty
  // sessionStorage on a new device would otherwise lose the carry). An
  // explicit `?from=` (e.g. the chooser's /redeem?code= deep link) always
  // wins over the pending-code fallback.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const target =
      from === '/' && user?.pendingRedeemCode
        ? `/redeem?code=${encodeURIComponent(user.pendingRedeemCode)}`
        : from;
    navigate(target, { replace: true });
  }, [status, from, navigate, user]);

  async function onCredentialsSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setNeedsEmailVerification(false);
    setResendStatus(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.status === 'totp_required') {
        setLoginToken(result.loginToken);
        setStep('totp');
      }
      // Authenticated (no TOTP): the post-auth effect navigates once the
      // AuthContext commits the identity - it routes to the pending claim when
      // one is on file (OCP-515), which this handler's `user` closure cannot
      // see yet (state is committed after this await returns).
    } catch (err) {
      setError(loginErrorMessage(err, t));
      if (err instanceof ApiError && err.code === 'email_not_verified') {
        setNeedsEmailVerification(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onResendVerification(): Promise<void> {
    if (resendStatus === 'pending' || !email) return;
    setResendStatus('pending');
    try {
      await api('/v1/portal/auth/email-verification/resend', {
        method: 'POST',
        body: { email: email.trim().toLowerCase() },
      });
      // Backend always returns 200 (don't leak whether the email
      // exists). Show success regardless - same enumeration-defence
      // posture as the password-reset request.
      setResendStatus('sent');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setResendStatus('rate_limited');
      } else {
        setResendStatus('failed');
      }
    }
  }

  async function onTotpSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting || !loginToken) return;
    setError(null);
    setSubmitting(true);
    try {
      await loginTotp(loginToken, totpCode.trim());
      // Post-auth navigation handled by the effect (OCP-515 pending-claim aware).
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t('error.totpMismatch'));
      } else if (err instanceof ApiError && err.code === 'login_token_expired') {
        setError(t('error.loginExpired'));
        setStep('credentials');
        setLoginToken(null);
        setTotpCode('');
      } else if (err instanceof ApiError && err.status === 429) {
        setError(t('error.tooManyAttempts'));
      } else {
        setError(t('error.totpGeneric'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-gray-800 px-4 text-gray-900 dark:text-gray-100">
      <div className="w-full max-w-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <OrbotoLockup className="h-7 w-auto" />
          <div className="text-center text-xs text-gray-500">{t('header')}</div>
        </div>

        {step === 'credentials' && (
          <form onSubmit={onCredentialsSubmit} className="flex flex-col gap-4" noValidate>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{t('email')}</span>
              <input
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{t('password')}</span>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            </label>

            {error && (
              <div
                role="alert"
                className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
              >
                {error}
                {needsEmailVerification && (
                  <div className="mt-2">
                    {resendStatus === 'sent' ? (
                      <p
                        data-testid="resend-success"
                        className="text-xs text-emerald-700 dark:text-emerald-300"
                      >
                        <Trans t={t} i18nKey="resend.sent" values={{ email }} components={{ strong: <strong /> }} />
                      </p>
                    ) : resendStatus === 'rate_limited' ? (
                      <p
                        data-testid="resend-rate-limited"
                        className="text-xs text-amber-800 dark:text-amber-300"
                      >
                        {t('resend.rateLimited')}
                      </p>
                    ) : resendStatus === 'failed' ? (
                      <p className="text-xs text-red-700 dark:text-red-300">
                        {t('resend.failed')}
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void onResendVerification()}
                        disabled={resendStatus === 'pending' || !email}
                        data-testid="resend-verification-button"
                        className="text-xs font-medium text-red-700 dark:text-red-300 underline disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        {resendStatus === 'pending' ? t('resend.sending') : t('resend.button')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submitting ? t('signingIn') : t('signIn')}
            </button>

            <div className="flex items-center justify-between text-sm">
              <Link to="/password-reset" className="text-gray-500 hover:text-brand-500">
                {t('forgotPassword')}
              </Link>
              <Link to="/signup" className="text-gray-500 hover:text-brand-500">
                {t('createAccount')}
              </Link>
            </div>
          </form>
        )}

        {step === 'totp' && (
          <form onSubmit={onTotpSubmit} className="flex flex-col gap-4" noValidate>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('totpIntro')}
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{t('verificationCode')}</span>
              <input
                type="text"
                id="totp"
                name="totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                pattern="[0-9]{6,8}"
                required
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-center text-lg tracking-[0.4em] focus:border-brand-500 focus:outline-none"
                placeholder={t('totpPlaceholder')}
              />
              <span className="text-xs text-gray-500">
                {t('lostAuthenticator')}
              </span>
            </label>

            {error && (
              <div
                role="alert"
                className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submitting ? t('verifying') : t('verify')}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep('credentials');
                setLoginToken(null);
                setTotpCode('');
                setError(null);
              }}
              className="text-center text-sm text-gray-500 hover:text-brand-500"
            >
              {t('backToSignIn')}
            </button>
          </form>
        )}
        {/* Imprint, privacy, terms and the DB-IP credit (OCP-848) on every page. */}
        <LegalFooter className="mt-6" />
      </div>
    </div>
  );
}

function loginErrorMessage(err: unknown, t: TFunction<'login'>): string {
  if (err instanceof ApiError && err.status === 401) {
    return t('error.wrongCredentials');
  }
  if (err instanceof ApiError && err.code === 'email_not_verified') {
    return t('error.emailNotVerified');
  }
  if (err instanceof ApiError && err.status === 429) {
    return t('error.tooManyAttempts');
  }
  return t('error.generic');
}
